import React, { useMemo, useState } from "react";
import CustomToolbar from "../global/CustomToolbar";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import TuneIcon from "@material-ui/icons/Tune";
import ConfirmDialog from "./ConfirmDialog";

function PTToolbar({ title, onBack, actions }) {
  const headerButtons = [];
  if (onBack) {
    headerButtons.push({ icon: <KeyboardBackspaceIcon />, title: "Back", handler: onBack });
  }
  (actions || []).forEach((a) => headerButtons.push(a));
  return <CustomToolbar title={title} headerButtons={headerButtons} />;
}

const P_COLORS = [
  "#2563eb", "#059669", "#d97706", "#7c3aed", "#ea580c",
  "#dc2626", "#0891b2", "#be185d", "#4f46e5", "#0d9488",
  "#65a30d", "#6366f1", "#f59e0b",
];
const colorFor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return P_COLORS[h % P_COLORS.length];
};

const codeFor = (name) =>
  (name || "").replace(/[^A-Z0-9]/gi, "").slice(0, 3).toUpperCase() || "LOC";

/* ═══════════════════════════════════════════════════════════
   Settings landing
   ══════════════════════════════════════════════════════════ */

export function SettingsMain({ nav, locations }) {
  const partnerSet = new Set();
  locations.forEach((l) => (l.investors || []).forEach((i) => partnerSet.add(i.name)));

  return (
    <>
      <PTToolbar title="Settings" onBack={() => nav("landing")} />
      <div className="pt-content">
        <div className="pt-lb">Configuration</div>
        <button className="pt-listbtn" onClick={() => nav("sLocs")}>
          <div>
            <div className="name"><span role="img" aria-label="location pin">📍</span> Locations</div>
            <div className="meta">{locations.length} configured</div>
          </div>
          <span style={{ color: "var(--pt-muted)" }}>›</span>
        </button>
        <button className="pt-listbtn" onClick={() => nav("sPartners")}>
          <div>
            <div className="name"><span role="img" aria-label="people">👥</span> Partners</div>
            <div className="meta">{partnerSet.size} across all locations</div>
          </div>
          <span style={{ color: "var(--pt-muted)" }}>›</span>
        </button>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Locations list — inline creation + cards that open detail
   ══════════════════════════════════════════════════════════ */

export function LocsList({ nav, locations, setLocations, api, isAdmin }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const createLocation = () => {
    const name = newName.trim();
    if (!name) {
      setError("Enter a name first.");
      return;
    }
    setSaving(true);
    setError(null);
    api.saveLocation(
      { name },
      (res) => {
        setSaving(false);
        if (!res?.success || !res?.location) {
          setError(res?.message || "Failed to create location.");
          return;
        }
        const newLoc = { investors: [], ...res.location };
        setLocations((prev) => [...prev, newLoc]);
        setCreating(false);
        setNewName("");
        nav("sLocDetail", { loc: newLoc.locationId });
      },
      (err) => {
        setSaving(false);
        setError(typeof err === "string" ? err : "Failed to create location.");
      },
    );
  };

  // + New is admin-only
  const headerActions = isAdmin
    ? [
        {
          icon: <AddCircleIcon />,
          title: creating ? "Cancel" : "New location",
          handler: () => {
            setCreating((v) => !v);
            setError(null);
          },
        },
      ]
    : [];

  return (
    <>
      <PTToolbar
        title="Locations"
        onBack={() => nav("settings")}
        actions={headerActions}
      />
      <div className="pt-content">
        {creating && (
          <div className="pt-card" style={{ borderColor: "var(--pt-teal)", boxShadow: "var(--pt-shadow-md)" }}>
            <div className="pt-lb" style={{ color: "var(--pt-teal)" }}>New location</div>
            <input
              autoFocus
              type="text"
              placeholder="Location name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createLocation()}
              style={inputStyle}
            />
            {error && <div className="pt-error" style={{ marginTop: 8 }}>{error}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                onClick={createLocation}
                disabled={saving || !newName.trim()}
                style={primaryBtnStyle(!saving && !!newName.trim())}
              >
                {saving ? "Creating…" : "Create & assign partners"}
              </button>
              <button
                onClick={() => { setCreating(false); setNewName(""); setError(null); }}
                style={ghostBtnStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {locations.length === 0 && !creating && (
          <div className="pt-empty">No locations yet. Tap the + icon to create one.</div>
        )}

        {locations.map((loc) => {
          const code = codeFor(loc.name);
          const owners = (loc.investors || []).filter((i) => !i.isManager);
          const pct = owners.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
          const ok = owners.length > 0 && Math.abs(pct - 100) < 0.01;
          return (
            <button
              key={loc.locationId}
              className="pt-listbtn"
              onClick={() => nav("sLocDetail", { loc: loc.locationId })}
              style={{ alignItems: "flex-start" }}
            >
              <div style={{ display: "flex", gap: 12, flex: 1 }}>
                <div className="pt-loc-badge active" style={{ width: 42, height: 42, flexShrink: 0 }}>
                  {code}
                </div>
                <div style={{ textAlign: "left", flex: 1 }}>
                  <div className="name" style={{ fontSize: 15 }}>{loc.name}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                    {owners.length === 0 ? (
                      <span style={{ color: "var(--pt-amber-txt)", fontSize: 11, fontWeight: 500 }}>
                        No partners assigned
                      </span>
                    ) : (
                      owners.map((inv) => (
                        <span key={inv.investorId} style={partnerPill(colorFor(inv.name))}>
                          {inv.name} {Number(inv.percentage) || 0}%
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {owners.length > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    color: ok ? "var(--pt-green)" : "var(--pt-red)",
                    background: ok ? "#ecfdf5" : "#fef2f2",
                    border: `1px solid ${ok ? "#a7f3d0" : "#fecaca"}`,
                    padding: "3px 8px", borderRadius: 10,
                  }}>
                    {pct}%
                  </span>
                )}
                <span style={{ color: "var(--pt-muted)", fontSize: 16 }}>›</span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Location detail — partner management
   ══════════════════════════════════════════════════════════ */

export function LocDetail({ nav, locationId, locations, setLocations, api, isAdmin }) {
  const location = useMemo(
    () => locations.find((l) => l.locationId === locationId) || null,
    [locations, locationId],
  );
  const [error, setError] = useState(null);
  const [savingOp, setSavingOp] = useState(null); // id of row being saved / 'new' / 'rename' / 'delete'
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(location?.name || "");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [newPartnerName, setNewPartnerName] = useState("");
  const [newIsManager, setNewIsManager] = useState(false);
  const [newThreshold, setNewThreshold] = useState("");
  // dialog state — one at a time is enough for settings flows
  const [confirm, setConfirm] = useState(null); // { kind: 'location' | 'investor', target?: investor }

  if (!location) {
    return (
      <>
        <PTToolbar title="Location" onBack={() => nav("sLocs")} />
        <div className="pt-content">
          <div className="pt-empty">Location not found.</div>
        </div>
      </>
    );
  }

  const investors = location.investors || [];
  const owners = investors.filter((i) => !i.isManager);
  const ownerTotal = owners.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
  const remaining = Math.max(0, 100 - ownerTotal);

  // Every partner name that already exists anywhere, excluding those already on this location
  const allNames = new Set();
  locations.forEach((l) => (l.investors || []).forEach((i) => allNames.add(i.name)));
  const usedHere = new Set(investors.map((i) => i.name));
  const availableNames = Array.from(allNames).filter((n) => !usedHere.has(n));

  // helpers — mutate lifted locations state after successful API calls
  const patchLocation = (patch) =>
    setLocations((prev) =>
      prev.map((l) => (l.locationId === locationId ? { ...l, ...patch } : l)),
    );
  const patchInvestors = (fn) => patchLocation({ investors: fn(investors) });

  const doRename = () => {
    const name = renameValue.trim();
    if (!name || name === location.name) { setRenameOpen(false); return; }
    setSavingOp("rename");
    setError(null);
    api.renameLocation(
      { locationId, name },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Rename failed"); return; }
        patchLocation({ name: res.location?.name || name });
        setRenameOpen(false);
      },
      (err) => { setSavingOp(null); setError(typeof err === "string" ? err : "Rename failed"); },
    );
  };

  const doDeleteLocation = () => {
    setSavingOp("delete");
    setError(null);
    api.deleteLocation(
      { locationId },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Delete failed"); setConfirm(null); return; }
        setConfirm(null);
        setLocations((prev) => prev.filter((l) => l.locationId !== locationId));
        nav("sLocs");
      },
      (err) => {
        setSavingOp(null);
        setConfirm(null);
        setError(typeof err === "string" ? err : "Delete failed");
      },
    );
  };

  const addPartner = (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    const asManager = newIsManager;
    // managers are deducted BEFORE the owner split, so they don't take owner %.
    const pct = asManager ? 0 : remaining;
    const threshold = asManager ? Number(newThreshold) || 0 : 0;
    setSavingOp("new");
    setError(null);
    api.saveInvestor(
      {
        locationId,
        name: trimmed,
        percentage: pct,
        profitThreshold: threshold,
        isManager: asManager,
      },
      (res) => {
        setSavingOp(null);
        if (!res?.success || !res?.investor) { setError(res?.message || "Add failed"); return; }
        patchInvestors((list) => [...list, res.investor]);
        setNewPartnerName("");
        setNewIsManager(false);
        setNewThreshold("");
        setShowAddPanel(false);
      },
      (err) => { setSavingOp(null); setError(typeof err === "string" ? err : "Add failed"); },
    );
  };

  // Apply any subset of { percentage, profitThreshold, isManager } to an existing investor.
  const applyUpdate = (inv, patch) => {
    const merged = {
      investorId: inv.investorId,
      name: inv.name,
      percentage: patch.percentage !== undefined ? Number(patch.percentage) || 0 : Number(inv.percentage) || 0,
      profitThreshold: patch.profitThreshold !== undefined ? Number(patch.profitThreshold) || 0 : Number(inv.profitThreshold) || 0,
      isManager: patch.isManager !== undefined ? !!patch.isManager : !!inv.isManager,
    };
    // When flipping to manager, drop the owner percentage to 0; when flipping back, leave pct to the user.
    if (patch.isManager === true) merged.percentage = 0;
    setSavingOp(inv.investorId);
    setError(null);
    api.updateInvestor(
      merged,
      (res) => {
        setSavingOp(null);
        if (!res?.success || !res?.investor) { setError(res?.message || "Update failed"); return; }
        patchInvestors((list) =>
          list.map((i) => (i.investorId === inv.investorId ? res.investor : i)),
        );
      },
      (err) => { setSavingOp(null); setError(typeof err === "string" ? err : "Update failed"); },
    );
  };

  const removePartner = (inv) => {
    setSavingOp(inv.investorId);
    setError(null);
    api.deleteInvestor(
      { investorId: inv.investorId },
      (res) => {
        setSavingOp(null);
        if (!res?.success) { setError(res?.message || "Delete failed"); setConfirm(null); return; }
        setConfirm(null);
        patchInvestors((list) => list.filter((i) => i.investorId !== inv.investorId));
      },
      (err) => {
        setSavingOp(null);
        setConfirm(null);
        setError(typeof err === "string" ? err : "Delete failed");
      },
    );
  };

  const totalOk = Math.abs(ownerTotal - 100) < 0.01;

  // Destructive + edit actions are admin-only. Non-admins see the screen read-only.
  const headerActions = isAdmin
    ? [
        {
          icon: <DeleteIcon />,
          title: "Delete location",
          handler: () => setConfirm({ kind: "location" }),
          disabled: savingOp === "delete",
        },
      ]
    : [];

  return (
    <>
      <PTToolbar
        title={location.name}
        onBack={() => nav("sLocs")}
        actions={headerActions}
      />
      <div className="pt-content">
        {error && <div className="pt-error">{error}</div>}

        {/* Header card */}
        <div className="pt-card" style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="pt-loc-badge active" style={{ width: 52, height: 52, fontSize: 13 }}>
            {codeFor(location.name)}
          </div>
          <div style={{ flex: 1 }}>
            {!renameOpen ? (
              <>
                <div style={{ fontSize: 20, fontWeight: 700, color: "var(--pt-text)" }}>{location.name}</div>
                {isAdmin && (
                  <button
                    onClick={() => { setRenameValue(location.name); setRenameOpen(true); }}
                    style={linkBtnStyle}
                  >
                    Rename
                  </button>
                )}
              </>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  autoFocus
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doRename()}
                  style={{ ...inputStyle, flex: 1, marginTop: 0 }}
                />
                <button
                  onClick={doRename}
                  disabled={savingOp === "rename"}
                  style={primaryBtnStyle(savingOp !== "rename")}
                >
                  {savingOp === "rename" ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => { setRenameOpen(false); setRenameValue(location.name); }}
                  style={ghostBtnStyle}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div
            style={{
              fontSize: 11, fontWeight: 700,
              color: totalOk ? "var(--pt-green)" : ownerTotal === 0 ? "var(--pt-muted)" : "var(--pt-red)",
              background: totalOk ? "#ecfdf5" : ownerTotal === 0 ? "var(--pt-card-alt)" : "#fef2f2",
              border: `1px solid ${totalOk ? "#a7f3d0" : ownerTotal === 0 ? "var(--pt-border)" : "#fecaca"}`,
              padding: "4px 10px", borderRadius: 10,
            }}
          >
            {ownerTotal}%
          </div>
        </div>

        {/* Existing investors */}
        {investors.length === 0 && (
          <div className="pt-empty" style={{ padding: "30px 10px" }}>
            No partners on this location yet. Add one below.
          </div>
        )}
        {investors.map((inv) => (
          <InvestorRow
            key={inv.investorId}
            inv={inv}
            saving={savingOp === inv.investorId}
            canEdit={isAdmin}
            onUpdate={(patch) => applyUpdate(inv, patch)}
            onDelete={() => setConfirm({ kind: "investor", target: inv })}
          />
        ))}

        {/* Add panel — admin-only */}
        {!isAdmin ? null : !showAddPanel ? (
          <button
            onClick={() => setShowAddPanel(true)}
            className="pt-listbtn"
            style={{
              justifyContent: "center",
              borderStyle: "dashed",
              color: "var(--pt-teal)",
              background: "transparent",
              boxShadow: "none",
            }}
          >
            + Add partner
          </button>
        ) : (
          <div className="pt-card">
            {/* Manager + threshold toggle — applies to whichever add method is used */}
            <div
              style={{
                background: newIsManager ? "var(--pt-mgr-bg)" : "var(--pt-card-alt)",
                border: `1px solid ${newIsManager ? "#ddd6fe" : "var(--pt-border)"}`,
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 12,
                transition: "background 0.15s, border-color 0.15s",
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={newIsManager}
                  onChange={(e) => setNewIsManager(e.target.checked)}
                  style={{ accentColor: "var(--pt-purple)", width: 16, height: 16, cursor: "pointer" }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pt-text)" }}>
                    Add as manager
                  </div>
                  <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2, lineHeight: 1.4 }}>
                    Manager cut is deducted from gross profit <i>before</i> the owner split.
                  </div>
                </div>
              </label>
              {newIsManager && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  <label style={{ fontSize: 12, color: "var(--pt-text-2)", fontWeight: 600, flexShrink: 0 }}>
                    Profit threshold
                  </label>
                  <div style={{ position: "relative", flex: 1 }}>
                    <span style={{
                      position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                      color: "var(--pt-muted)", fontSize: 13, fontWeight: 600,
                    }}>$</span>
                    <input
                      type="number"
                      placeholder="0"
                      value={newThreshold}
                      onChange={(e) => setNewThreshold(e.target.value)}
                      style={{ ...inputStyle, marginTop: 0, paddingLeft: 24, fontVariantNumeric: "tabular-nums" }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="pt-lb">Select existing partner</div>
            {availableNames.length === 0 ? (
              <div style={{ color: "var(--pt-muted)", fontSize: 12, marginBottom: 8 }}>
                All known partners are already on this location.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {availableNames.map((n) => (
                  <button key={n} onClick={() => addPartner(n)} style={pillBtnStyle(colorFor(n))}>
                    {n}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--pt-border)" }} />
              <span style={{ color: "var(--pt-muted)", fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: "var(--pt-border)" }} />
            </div>
            <div className="pt-lb">Create new partner</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="Partner name"
                value={newPartnerName}
                onChange={(e) => setNewPartnerName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addPartner(newPartnerName)}
                style={{ ...inputStyle, flex: 1, marginTop: 0 }}
              />
              <button
                onClick={() => addPartner(newPartnerName)}
                disabled={savingOp === "new" || !newPartnerName.trim()}
                style={primaryBtnStyle(savingOp !== "new" && !!newPartnerName.trim())}
              >
                {savingOp === "new" ? "Adding…" : "Add"}
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "var(--pt-muted)" }}>
              {newIsManager
                ? `Will be added as a manager${(Number(newThreshold) || 0) > 0 ? ` with $${Number(newThreshold).toLocaleString("en-US")} threshold` : ""}. Percentage starts at 0%.`
                : `Owner will be added at ${remaining}% (remaining share). Adjust after.`}
            </div>
            <button
              onClick={() => {
                setShowAddPanel(false);
                setNewPartnerName("");
                setNewIsManager(false);
                setNewThreshold("");
              }}
              style={{ ...ghostBtnStyle, marginTop: 10 }}
            >
              Close
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        variant="danger"
        title={
          confirm?.kind === "location"
            ? "Delete this location?"
            : confirm?.kind === "investor"
            ? "Remove partner from location?"
            : ""
        }
        message={
          confirm?.kind === "location"
            ? `"${location.name}" and every partner assignment on it will be permanently removed. Historical profit entries keyed to this location may be affected.`
            : confirm?.kind === "investor" && confirm.target
            ? `${confirm.target.name} will be removed from ${location.name}. Their ${Number(confirm.target.percentage) || 0}% share will no longer be counted here.`
            : ""
        }
        confirmLabel={confirm?.kind === "location" ? "Delete location" : "Remove partner"}
        busy={savingOp === "delete" || (confirm?.kind === "investor" && savingOp === confirm.target?.investorId)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind === "location") doDeleteLocation();
          else if (confirm?.kind === "investor" && confirm.target) removePartner(confirm.target);
        }}
      />
    </>
  );
}

function InvestorRow({ inv, saving, onUpdate, onDelete, canEdit = true }) {
  const [pct, setPct] = useState(String(inv.percentage ?? 0));
  const [threshold, setThreshold] = useState(String(inv.profitThreshold ?? 0));
  const [expanded, setExpanded] = useState(false);

  // Keep local edits in sync if the server response changes the underlying value.
  React.useEffect(() => { setPct(String(inv.percentage ?? 0)); }, [inv.percentage]);
  React.useEffect(() => { setThreshold(String(inv.profitThreshold ?? 0)); }, [inv.profitThreshold]);

  const c = colorFor(inv.name);
  const pctNum = Number(pct);
  const pctDirty = Number.isFinite(pctNum) && pctNum !== Number(inv.percentage);
  const thrNum = Number(threshold);
  const thrDirty = Number.isFinite(thrNum) && thrNum !== Number(inv.profitThreshold);

  const commitPct = () => { if (pctDirty) onUpdate({ percentage: pctNum }); };
  const commitThreshold = () => { if (thrDirty) onUpdate({ profitThreshold: thrNum }); };

  return (
    <div
      className="pt-card compact"
      style={{ borderLeft: `4px solid ${c}`, padding: 0, overflow: "hidden" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${c}15`, border: `1.5px solid ${c}40`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: c, fontSize: 14, fontWeight: 800,
          flexShrink: 0,
        }}>
          {inv.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pt-text)", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <span>{inv.name}</span>
            {inv.isManager && <span style={managerBadge}>Manager</span>}
          </div>
          {inv.isManager && (Number(inv.profitThreshold) || 0) > 0 && (
            <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2 }}>
              Threshold ${Number(inv.profitThreshold).toLocaleString("en-US")}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--pt-card-alt)", padding: "4px 6px 4px 10px", borderRadius: 8 }}>
          {canEdit ? (
            <input
              type="number"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              onBlur={commitPct}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              disabled={saving}
              style={{
                width: 56, background: "transparent", border: "none",
                color: "var(--pt-text)", fontSize: 15, fontWeight: 700,
                textAlign: "right", outline: "none",
                fontFamily: "inherit", fontVariantNumeric: "tabular-nums",
              }}
            />
          ) : (
            <span style={{ color: "var(--pt-text)", fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums", paddingRight: 2, minWidth: 40, textAlign: "right" }}>
              {Number(inv.percentage) || 0}
            </span>
          )}
          <span style={{ color: "var(--pt-muted)", fontSize: 13, fontWeight: 600 }}>%</span>
        </div>
        {canEdit && (
          <>
            <button
              onClick={() => setExpanded((v) => !v)}
              disabled={saving}
              style={{
                background: expanded ? "var(--pt-navy)" : "var(--pt-card)",
                color: expanded ? "#fff" : "var(--pt-text-2)",
                border: `1px solid ${expanded ? "var(--pt-navy)" : "var(--pt-border)"}`,
                width: 38, height: 38, borderRadius: 10,
                cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.15s, color 0.15s, border-color 0.15s",
              }}
              title="Manager options"
              aria-label="Manager options"
            >
              <TuneIcon style={{ fontSize: 20 }} />
            </button>
            <button
              onClick={onDelete}
              disabled={saving}
              style={{
                background: "#fef2f2", border: "1px solid #fecaca",
                width: 38, height: 38, borderRadius: 10, cursor: "pointer",
                color: "var(--pt-red)", display: "flex", alignItems: "center", justifyContent: "center",
              }}
              title="Remove partner"
            >
              <DeleteIcon style={{ fontSize: 20 }} />
            </button>
          </>
        )}
      </div>

      {canEdit && expanded && (
        <div style={{
          borderTop: "1px solid var(--pt-border)",
          background: "var(--pt-card-alt)",
          padding: "12px 14px",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!inv.isManager}
              onChange={(e) => onUpdate({ isManager: e.target.checked })}
              disabled={saving}
              style={{ accentColor: "var(--pt-purple)", width: 16, height: 16, cursor: "pointer" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--pt-text)" }}>
                Manager
              </div>
              <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2, lineHeight: 1.4 }}>
                Manager cut is deducted from gross profit before the owner split. Owner % resets to 0 when enabled.
              </div>
            </div>
          </label>
          {inv.isManager && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 12, color: "var(--pt-text-2)", fontWeight: 600, flexShrink: 0 }}>
                Profit threshold
              </label>
              <div style={{ position: "relative", flex: 1 }}>
                <span style={{
                  position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                  color: "var(--pt-muted)", fontSize: 13, fontWeight: 600,
                }}>$</span>
                <input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  onBlur={commitThreshold}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  disabled={saving}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: "var(--pt-card)",
                    border: "1px solid var(--pt-border)",
                    borderRadius: 8,
                    padding: "8px 10px 8px 24px",
                    fontSize: 13, color: "var(--pt-text)",
                    outline: "none", fontFamily: "inherit",
                    fontVariantNumeric: "tabular-nums",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Partners list — grouped by name across locations
   ══════════════════════════════════════════════════════════ */

export function PartnersList({ nav, locations }) {
  const partners = useMemo(() => {
    const map = new Map();
    locations.forEach((l) => {
      (l.investors || []).forEach((inv) => {
        if (!map.has(inv.name)) {
          map.set(inv.name, {
            name: inv.name,
            locations: [],
            isManager: inv.isManager,
            totalPct: 0,
          });
        }
        const p = map.get(inv.name);
        p.locations.push({ name: l.name, pct: Number(inv.percentage) || 0 });
        p.totalPct += Number(inv.percentage) || 0;
        if (inv.isManager) p.isManager = true;
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [locations]);

  return (
    <>
      <PTToolbar title="Partners" onBack={() => nav("settings")} />
      <div className="pt-content">
        {partners.length === 0 && <div className="pt-empty">No partners yet.</div>}
        {partners.map((p) => {
          const c = colorFor(p.name);
          return (
            <div key={p.name} className="pt-card compact" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: `${c}15`, border: `1.5px solid ${c}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: c, fontSize: 16, fontWeight: 800, flexShrink: 0,
              }}>
                {p.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {p.name}
                  {p.isManager && <span style={managerBadge}>Manager</span>}
                </div>
                <div style={{ color: "var(--pt-muted)", fontSize: 11, marginTop: 2 }}>
                  {p.locations.length} location{p.locations.length !== 1 ? "s" : ""}
                </div>
                {p.locations.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                    {p.locations.map((l) => (
                      <span key={l.name} style={partnerPill(c)}>
                        {l.name} · {l.pct}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ─── Inline style helpers (small enough to keep near usage) ── */

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: "var(--pt-card)",
  border: "1px solid var(--pt-border)",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  color: "var(--pt-text)",
  outline: "none",
  marginTop: 8,
  fontFamily: "inherit",
};

const primaryBtnStyle = (active) => ({
  background: active ? "var(--pt-teal)" : "var(--pt-border)",
  border: "none",
  color: active ? "#fff" : "var(--pt-muted)",
  padding: "10px 16px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: active ? "pointer" : "default",
  whiteSpace: "nowrap",
});
const ghostBtnStyle = {
  background: "var(--pt-card)",
  border: "1px solid var(--pt-border)",
  color: "var(--pt-text-2)",
  padding: "10px 14px",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};
const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "var(--pt-teal)",
  fontSize: 12,
  fontWeight: 600,
  padding: 0,
  marginTop: 2,
  cursor: "pointer",
  fontFamily: "inherit",
};
const partnerPill = (c) => ({
  background: `${c}12`,
  color: c,
  border: `1px solid ${c}30`,
  padding: "2px 8px",
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
});
const pillBtnStyle = (c) => ({
  background: `${c}10`,
  color: c,
  border: `1px solid ${c}30`,
  padding: "6px 12px",
  borderRadius: 10,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
});
const managerBadge = {
  background: "var(--pt-mgr-bg)",
  color: "var(--pt-purple)",
  border: "1px solid #ddd6fe",
  fontSize: 9,
  fontWeight: 700,
  padding: "2px 7px",
  borderRadius: 4,
  marginLeft: 8,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  verticalAlign: "middle",
};

// keep exported even if not used elsewhere — avoids orphan-import lint
export const _icons = { SaveIcon };
